import * as React from 'react';
import {
  FeedbackAuthenticationType,
  FeedbackInitData,
  FeedbackType,
  FeedbackUiType,
  IFeedbackInitOptions,
  InAppFeedbackQuestionUiBehaviour,
  InAppFeedbackQuestionUiType,
  InAppFeedbackScenarioType,
  initializeInAppFeedback,
  openFeedback,
  updateFeedbackOptions,
} from '@ms/inapp-feedback-loader';
import { useLocalizedStringsForLocale } from 'common/hooks/useLocalizedStrings';
import { ThemeType } from 'common/theme/ThemeTypes';
import { pageDataService } from 'common/utilities/PageDataService';
import { usePageLoadTracker } from 'common/utilities/PageLoadTracker';
import { logClick } from 'common/utilities/telemetryHelpers';
import { getLocale } from 'i18n';
import { dashboardPath } from 'Routes';
import { useNotifications } from 'store/layout/hooks/useNotifications';

import { appIds, clientName, hostName } from './OcvConstants';

interface IDigestFeedbackPageProps {
  onDismiss?: () => void;
  disableRating?: boolean;
}

/**
 * The digest feedback page, which is an UNAUTHENTICATED page,
 * is used to collect feedback from users via a link in the digest email.
 * This page uses Centro's OCV in-app feedback module.
 *
 * Documentation:
 * https://projectcentro.azurewebsites.net/docs/#/consume/hvcs/ocv-inapp-feedback
 *
 * @param props
 * @returns {React.ReactElement} the digest feedback page
 */
export const DigestFeedbackPage = (props: IDigestFeedbackPageProps) => {
  const { onDismiss, disableRating } = props;

  const { toastGenericError } = useNotifications();

  const query = new URLSearchParams(window.location.search);
  const campaignId = query.get('campaign');

  const locale = getLocale();
  const { t, ready: currentLocaleIsReady } = useLocalizedStringsForLocale(locale);
  const { t: tInEnglish, ready: englishLocaleIsReady } = useLocalizedStringsForLocale('en');

  const feedbackId = 'inAppFeedback_monthlyDigest';

  usePageLoadTracker('OcvDigestFeedbackPageView', []);

  const answerStrings = React.useMemo(
    () => [
      // i18next-extract-disable
      {
        displayedStringInEnglish: tInEnglish('OcvFeedback.DigestFeedback.AnswerStar', { count: 1 }),
        displayedString: t('OcvFeedback.DigestFeedback.AnswerStar', { count: 1 }),
      },
      {
        displayedStringInEnglish: tInEnglish('OcvFeedback.DigestFeedback.AnswerStar', { count: 2 }),
        displayedString: t('OcvFeedback.DigestFeedback.AnswerStar', { count: 2 }),
      },
      {
        displayedStringInEnglish: tInEnglish('OcvFeedback.DigestFeedback.AnswerStar', { count: 3 }),
        displayedString: t('OcvFeedback.DigestFeedback.AnswerStar', { count: 3 }),
      },
      {
        displayedStringInEnglish: tInEnglish('OcvFeedback.DigestFeedback.AnswerStar', { count: 4 }),
        displayedString: t('OcvFeedback.DigestFeedback.AnswerStar', { count: 4 }),
      },
      {
        displayedStringInEnglish: tInEnglish('OcvFeedback.DigestFeedback.AnswerStar', { count: 5 }),
        displayedString: t('OcvFeedback.DigestFeedback.AnswerStar', { count: 5 }),
      },
      // i18next-extract-enable
    ],
    [t, tInEnglish]
  );

  const questionStrings = React.useMemo(
    () => ({
      displayedStringInEnglish: tInEnglish('OcvFeedback.DigestFeedback.RatingQuestion'),
      displayedString: t('OcvFeedback.DigestFeedback.RatingQuestion'),
    }),
    [t, tInEnglish]
  );

  React.useEffect(() => {
    // Do not initialize OCV if translations are not yet ready
    if (!locale || !currentLocaleIsReady || !englishLocaleIsReady) {
      return;
    }

    // Must be mounted in DOM:
    const inAppFeedbackRef = document.getElementById(feedbackId);

    const generateToast = (error: unknown, id: string) => {
      toastGenericError(error, id, undefined, undefined, undefined, undefined, true);
    };

    const feedbackData: IFeedbackInitOptions = {
      appId: pageDataService.isProdEnvironment() ? appIds.prod : appIds.int,
      isProduction: pageDataService.isProdEnvironment(),
      clientName,
      authenticationType: FeedbackAuthenticationType.Unauthenticated,
      telemetry: {
        audienceGroup: 'TestAudienceGroup',
        sourceContext: campaignId ?? 'Unclassified',
        sourcePageName: 'DigestFeedbackPage',
      },
      callbackFunctions: {
        onDismiss: logClick('OcvDigestFeedbackCancelClick', () => {
          if (onDismiss) {
            onDismiss();
          } else {
            // use this instead of navigateTo() to avoid loading error page
            window.location.href = dashboardPath;
          }
        }),
        // Triggered when feedback failed to send due to internal error:
        onError: (error) => {
          generateToast(error, 'OcvDigestFeedbackErrorOnSubmit');
        },
        onSuccess: logClick('OcvDigestFeedbackSuccessClick', () => {}),
      },
      themeOptions: { baseTheme: ThemeType },
      feedbackConfig: {
        feedbackUiType: FeedbackUiType.Modal,
        initialFeedbackType: FeedbackType.Unclassified,
        invokeOnDismissOnEsc: true,
        isDisplayed: true,
        isEmailCollectionEnabled: true,
        isFileUploadEnabled: false,
        isScreenRecordingEnabled: false,
        isScreenshotEnabled: false,
        scenarioConfig: {
          isScenarioEnabled: true,
          scenarioType: InAppFeedbackScenarioType.Custom,
          questionDetails: disableRating
            ? undefined
            : {
                questionUiType: InAppFeedbackQuestionUiType.Rating,
                questionUiBehaviour: [InAppFeedbackQuestionUiBehaviour.CommentNotRequired],
                questionInstruction: questionStrings,
                questionOptions: answerStrings,
              },
        },
      },
    };

    const feedbackInfo: FeedbackInitData = {
      hostName,
      locale,
      inAppFeedbackRef: inAppFeedbackRef!,
    };

    const initializeFeedback = async () => {
      if (inAppFeedbackRef && hostName && locale) {
        try {
          await initializeInAppFeedback(feedbackInfo);
          updateFeedbackOptions(feedbackData);
          openFeedback(feedbackData);
        } catch (error) {
          generateToast(error, 'OcvDigestFeedbackErrorOnInitialization');
        }
      }
    };

    initializeFeedback();
  }, [
    answerStrings,
    campaignId,
    currentLocaleIsReady,
    disableRating,
    englishLocaleIsReady,
    locale,
    onDismiss,
    questionStrings,
    t,
    toastGenericError,
  ]);

  // Condition is to prevent a flash of OCV UI before redirect:
  return <div id={feedbackId} />;
};

export default DigestFeedbackPage;
